import React, { useEffect, useRef, useState } from "react";
import {
  CButton,
  CNavLink,
  CNav,
  CTabPane,
  CNavItem,
  CTabContent,
  CFormInput,
  CToast,
  CToastHeader,
  CToastBody,
  CToaster,
} from "@coreui/react";
import { useParams } from "react-router";
import { getStaticMenuPage } from "../../services/apiConfig/menu.service";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import MyEditor from "../../components/Editor/Editor";
import _, { uniqueId } from "lodash";
import API_SERVICES from "../../services";
import {
  LanguageData,
  StaticPageState,
  UpdateContentFunction,
} from "../../models/staticmenu.models";
import commonService from "../../services/common.service";
import handleResponse from "../../services/apiConfig/api.response.service";
import PageLoader from "../../components/PageLoader/PageLoader";
import { useNavigate } from "react-router-dom";
import { COMMON_CONSTANTS, Languages } from "../../constants/commonConstants";
import { Language, TitleError } from "../../models/channel.model";
import {
  ToastContent,
  getPageContent,
  languages,
} from "../../utils/editor.utils";

const StaticMenuItem: React.FC = () => {
  const [activeKey, setActiveKey] = useState(1);
  const [errors, setErrors] = useState<TitleError>({});
  const [editorId, setEditorId] = useState(uniqueId);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenToaster, setIsOpenToaster] = useState(false);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isTextValid, setIsTextValid] = useState(true);
  const [language, setLanguage] = useState("en");
  const [staticPage, setStaticPage] = useState<StaticPageState>({
    en: {
      title: "",

      text: "",
    },

    de: {
      title: "",

      text: "",
    },

    es: {
      title: "",

      text: "",
    },

    ru: {
      title: "",

      text: "",
    },
  });
  const [textMessage, setTextMessage] = useState("");
  const [titleMessage, setTitleMessage] = useState("");
  const toaster = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const params = useParams<{ uuid: string }>();

  useEffect(() => {
    getStaticMenuDetails();
  }, []);

  useEffect(() => {
    if (isOpenToaster) {
      ToastContent(isOpenToaster, errors);
      setIsOpenToaster(false);
    }
  }, [errors]);

  const getStaticMenuDetails = async () => {
    const data = await getStaticMenuPage(params.uuid as string);
    setEditorId(uniqueId);
    setStaticPage(prevStaticPage => {
      const list = _.cloneDeep(prevStaticPage);

      Object.keys(data.data).map((element: string, index: number) => {
        list[element] = data.data[element] ? data.data[element] : list[element];
      });
      return list;
    });
  };

  const handleCancel = () => {
    setStaticPage(staticPage);
    setIsTitleValid(true);
    setIsTextValid(true);
    setTitleMessage("");
    setTextMessage("");
    navigate(COMMON_CONSTANTS.PREVIOUS_PAGE);
  };

  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,

    language: keyof StaticPageState
  ) => {
    const newTitle = event.target.value;

    setStaticPage(prevState => ({
      ...prevState,

      [language]: {
        ...prevState[language],

        title: newTitle,
      },
    }));
    language === Languages.EN && setIsTitleValid(newTitle.trim() !== "");
    if (newTitle.trim() === "" && language === Languages.EN) {
      setTitleMessage("English title is required");
    } else {
      setTitleMessage("");
    }
  };

  const getTextContent = () => {
    return staticPage;
  };

  const handleSave = () => {
    setIsLoading(true);
    const test = getPageContent(staticPage);

    if (Object.keys(test).length > 0) {
      setErrors(test);
      setIsOpenToaster(true);
      setIsLoading(false);
    } else {
      const obj = {
        page_id: params.uuid as string,
        locales: getTextContent(),
      };

      API_SERVICES.MENUS.STATIC_MENU_DETAILS(obj).then((data: any) => {
        const responseData: any = handleResponse(data);

        if (responseData.ok) {
          commonService.toastService(responseData.data, "success");
        } else {
          commonService.toastService("failed to update static menu", "error");
        }
        setIsLoading(false);
      });
    }
  };

  const updateContent: UpdateContentFunction = newContent => {
    setStaticPage(prevStaticPage => {
      const list: any = _.cloneDeep(prevStaticPage);
      list[language].text = newContent;
      return list;
    });
    setIsTextValid(newContent.trim() !== "");
    if (newContent.trim() === "") {
      setTextMessage("Text is required");
    } else {
      setTextMessage("");
    }
  };

  return (
    <>
      <CNav variant="tabs" role="tablist">
        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === 1}
            component="button"
            role="tab"
            aria-controls="home-tab-pane"
            aria-selected={activeKey === 1}
            onClick={() => {
              setLanguage("en");

              setActiveKey(1);
            }}
          >
            English
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === 2}
            component="button"
            role="tab"
            aria-controls="profile-tab-pane"
            aria-selected={activeKey === 2}
            onClick={() => {
              setLanguage("de");

              setActiveKey(2);
            }}
          >
            German
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === 3}
            component="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected={activeKey === 3}
            onClick={() => {
              setLanguage("es");

              setActiveKey(3);
            }}
          >
            Spanish
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === 4}
            component="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected={activeKey === 4}
            onClick={() => {
              setLanguage("ru");

              setActiveKey(4);
            }}
          >
            Russian
          </CNavLink>
        </CNavItem>
      </CNav>

      <CTabContent>
        {Object.keys(staticPage).map(
          (element: keyof StaticPageState, index) => {
            return (
              <CTabPane
                key={index}
                role="tabpanel"
                aria-labelledby="home-tab-pane"
                visible={activeKey === Number(index) + 1}
              >
                <CFormInput
                  className="form-title"
                  type="text"
                  value={staticPage[element as keyof StaticPageState].title}
                  onChange={e =>
                    handleTitleChange(e, language as keyof StaticPageState)
                  }
                />
                {titleMessage && (
                  <div className="error-message">{titleMessage}</div>
                )}{" "}
                {staticPage ? (
                  <MyEditor
                    key={language + editorId}
                    updateContent={updateContent}
                    initialContent={
                      staticPage[element as keyof StaticPageState]
                        ? staticPage[element as keyof StaticPageState].text
                        : ""
                    }
                  />
                ) : (
                  <MyEditor
                    key="data2"
                    initialContent={""}
                    updateContent={updateContent}
                  />
                )}
                {textMessage && (
                  <div className="error-message">{textMessage}</div>
                )}{" "}
              </CTabPane>
            );
          }
        )}
      </CTabContent>
      <div className="mp-button-container">
        <CButton color="secondary" className="me-2" onClick={handleCancel}>
          Cancel
        </CButton>

        <CButton
          color="danger"
          onClick={handleSave}
          disabled={!isTitleValid || !isTextValid}
        >
          Save
        </CButton>
      </div>
      <PageLoader isLoading={isLoading} overlayOnly={false} />
    </>
  );
};

export default StaticMenuItem;
